import usePrivateAxios from "@/common/api/usePrivateAxios"
import Box from "@/common/components/Box"
import Button from "@/common/components/Button"
import TextField from "@/common/components/TextField"
import { useMutation } from "@tanstack/react-query"
import { ChevronsRight } from "lucide-react"
import { Link } from "react-router-dom"
import { ProductCategoryValidationSchema, createSchema } from "../../schemas/create.schema"
import { zodResolver } from "@hookform/resolvers/zod"
import { SubmitHandler, useForm } from "react-hook-form"
import { useState } from "react"
import Spinner from "@/common/components/Spinner"
import { AxiosError } from "axios"
import { useGoBack } from "@/common/hooks/useGoBack";
import GoBackButton from "@/common/components/GoBackButton";
import useUserPermissions from "@/common/hooks/useUserPermissions"
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper"

const Create = () => {
  const goBack = useGoBack();
  const privateAxios = usePrivateAxios();
  const [serverError, setServerError] = useState('');

  const { mutate, isPending } = useMutation({
    mutationKey: ['createProductCategory'],
    mutationFn: async (form: ProductCategoryValidationSchema) => privateAxios.post('/product_categories', { product_category: form }),
    onSuccess: () => {
      goBack({ fallback: '/product-categories' });
    },
    onError: (error: AxiosError<{errors: string}>) => {
      setServerError(error?.response?.data?.errors || 'generic');
    }
  });

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const hasCreateProductCategoryPermission = !isUserPermissionsPending ? hasUserPermission('Admin::ProductCategories::Create') : true

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<ProductCategoryValidationSchema>({
    resolver: zodResolver(createSchema)
  })

  const onSubmit: SubmitHandler<ProductCategoryValidationSchema> = (data) => mutate(data);

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-128px)]"
      statusCode={hasCreateProductCategoryPermission ? 200 : 403}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2 items-center">
            <GoBackButton fallback="/product-categories" />
            <p className="text-white-400 font-normal">Categoria de Produtos</p>
            <ChevronsRight color='#ffffffb3' />
            <h2>Nova categoria</h2>
          </div>
          <div className="flex gap-4">
            <Link to='/product-categories'><Button variant='outline-error'>Cancelar</Button></Link>
            <Button data-testid="create_products_categories" type="submit" className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]" >{isPending ? <Spinner className="h-6 mx-[23px] fill-white-800" /> : 'Confirmar'}</Button>
          </div>
        </div>
        <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
          {serverError === 'generic' && <p className="text-sm text-danger-soft">Ocorreu um erro no servidor, cheque os campos e tente novamente</p>}
          <p className="text-lg font-bold">Nova Categoria</p>
          <TextField
            label="Nome da categoria"
            data-testid="display_name_input"
            markAsRequired
            {...register('display_name')}
            errorMessage={
              serverError === 'Validation failed: Display name has already been taken'
              ? 'Produto com mesmo nome já existe'
              : errors?.display_name?.message
              ? errors?.display_name?.message
              : undefined
            }
          />
        </Box>
      </form>
    </ErrorMessageWrapper>
  )
}

export default Create
